<script setup lang="ts">
import { computed } from "vue";

import RecycledSection from "./RecycledSection.vue";
import SelectInputNew from "@/components/Reusables/Inputs/SelectInputNew.vue";
import { MaterialStageEnum } from "@/types/Views/MappingWizard/MaterialStageEnum";

// Titled "material stage" section (RTLCA-5030): pick the life-cycle stage and toggle reuse.
// Only one stage applies per material, so it's a single-select dropdown. The chosen stage is
// sent on save and persisted by the backend (RTLCA-5039).
const props = defineProps<{
  value: boolean | undefined; // isRecycled (reuse)
  stage: number | undefined; // MaterialStageEnum
  disabled: boolean;
}>();

const emits = defineEmits<{
  update: [payload: boolean];
  updateStage: [payload: number];
}>();

const stageOptions = [
  { value: MaterialStageEnum["New Construction"], label: "New construction" },
  { value: MaterialStageEnum.Existing, label: "Existing" },
  { value: MaterialStageEnum.Demolished, label: "Demolition" },
];

// Defaults to New Construction when the material has no stage yet; the setter emits back up so
// MaterialDetailView keeps its existing updateStage handler unchanged.
const stageModel = computed<number>({
  get: () => props.stage ?? MaterialStageEnum["New Construction"],
  set: (value) => {
    if (!props.disabled) emits("updateStage", value);
  },
});
</script>

<template>
  <div class="flex flex-col gap-3">
    <p class="t-caption-bold text-type-muted tracking-wide uppercase">Material stage</p>

    <!-- Fills its column rather than capping at 20rem: this section sits in the modal's wide left
         column (RTLCA-5181), where a fixed-width select left most of the row empty. -->
    <div class="w-full">
      <SelectInputNew
        v-model="stageModel"
        cast="number"
        :disabled="disabled">
        <option
          v-for="option in stageOptions"
          :key="option.value"
          :value="option.value">
          {{ option.label }}
        </option>
      </SelectInputNew>
    </div>

    <RecycledSection
      :value="value"
      :disabled="disabled"
      @update="(val: boolean) => emits('update', val)" />
  </div>
</template>
